import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";

export type Language = "es" | "en";

export type TranslationStatus = "idle" | "downloading" | "ready" | "unsupported";

const SOURCE_LANGUAGE: Language = "es";
const STORAGE_KEY = "app-language";

/** Feature flag: `REACT_APP_TRANSLATION_ENABLED=false` apaga la traducción (fuerza ES). */
export const isTranslationEnabled = (): boolean =>
  process.env.REACT_APP_TRANSLATION_ENABLED !== "false";

interface TranslationContextType {
  lang: Language;
  status: TranslationStatus;
  downloadProgress: number;
  enabled: boolean;
  setLang: (lang: Language) => void;
  translate: (text: string) => Promise<string>;
  getCached: (text: string) => string | undefined;
}

const TranslationContext = createContext<TranslationContextType | null>(null);

const readStoredLanguage = (): Language => {
  if (!isTranslationEnabled()) return "es";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "en" ? "en" : "es";
  } catch {
    return "es";
  }
};

const isTranslatorSupported = (): boolean =>
  typeof window !== "undefined" && "Translator" in window;

export const toSentenceCase = (text: string): string => {
  if (!text) return text;
  const match = text.match(/\p{L}/u);
  if (!match || match.index === undefined) return text;
  const i = match.index;
  return text.slice(0, i) + text[i].toUpperCase() + text.slice(i + 1).toLowerCase();
};

/** Números de cátedra habituales (I–XXXIX). No usa el alfabeto romano completo para no convertir palabras como "civil" en siglas. */
const ROMAN_NUMERAL = /^(?=.)(?:X{0,3})(?:IX|IV|V?I{0,3})$/i;

/**
 * Artículos, conjunciones y preposiciones. Van en minúscula en un título,
 * salvo cuando son la primera palabra.
 */
const SMALL_WORDS = new Set([
  "el", "la", "los", "las", "lo", "un", "una", "unos", "unas",
  "al", "del",
  "y", "e", "o", "u", "ni", "pero", "mas", "sino", "que", "aunque", "porque", "pues", "si", "como",
  "a", "ante", "bajo", "cabe", "con", "contra", "de", "desde", "durante", "en", "entre",
  "hacia", "hasta", "mediante", "para", "por", "según", "sin", "so", "sobre", "tras", "través", "vía",
  "an", "the",
  "and", "but", "or", "nor", "yet", "so", "if", "as", "because", "while", "although", "though",
  "of", "in", "on", "at", "to", "for", "with", "by", "from", "into", "onto", "over", "under",
  "after", "before", "between", "through", "within", "without", "about", "across", "against",
  "along", "around", "behind", "below", "beneath", "beside", "beyond", "despite", "except",
  "inside", "outside", "per", "plus", "than", "toward", "towards", "until", "upon", "versus",
  "vs", "via", "above", "among", "amongst",
]);

/**
 * Título: mayúscula en la primera palabra y en el resto, excepto artículos,
 * conjunciones y preposiciones.
 */
export const toTitleCase = (text: string): string => {
  if (!text) return text;
  let isFirstWord = true;
  return text.replace(/[\p{L}\p{N}]+(?:['’][\p{L}\p{N}]+)*/gu, (word) => {
    const first = isFirstWord;
    isFirstWord = false;
    if (ROMAN_NUMERAL.test(word)) return word.toUpperCase();
    const lower = word.toLowerCase();
    if (!first && SMALL_WORDS.has(lower)) return lower;
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  });
};

interface TranslationProviderProps {
  children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const enabled = isTranslationEnabled();
  const [lang, setLangState] = useState<Language>(readStoredLanguage);
  const [status, setStatus] = useState<TranslationStatus>(() => {
    if (!enabled) return "unsupported";
    return isTranslatorSupported() ? "idle" : "unsupported";
  });
  const [downloadProgress, setDownloadProgress] = useState(0);

  const translatorRef = useRef<Promise<TranslatorInstance> | null>(null);
  const memoryRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!enabled && lang !== "es") {
      setLangState("es");
    }
  }, [enabled, lang]);

  useEffect(() => {
    document.documentElement.lang = lang;
    if (!enabled) return;
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // localStorage no disponible: la preferencia simplemente no persiste.
    }
  }, [lang, enabled]);

  const getTranslator = useCallback((): Promise<TranslatorInstance> => {
    if (!enabled) {
      return Promise.reject(new Error("Traducción deshabilitada"));
    }

    if (translatorRef.current) return translatorRef.current;

    if (!isTranslatorSupported() || !Translator) {
      setStatus("unsupported");
      return Promise.reject(new Error("Translator API no soportada"));
    }

    const target: Language = "en";
    const creation = Translator.availability({
      sourceLanguage: SOURCE_LANGUAGE,
      targetLanguage: target,
    })
      .then((availability) => {
        if (availability === "unavailable") {
          setStatus("unsupported");
          throw new Error("Par de idiomas no disponible");
        }
        if (availability !== "available") {
          setStatus("downloading");
        }
        return Translator!.create({
          sourceLanguage: SOURCE_LANGUAGE,
          targetLanguage: target,
          monitor: (monitor) => {
            monitor.addEventListener("downloadprogress", (event) => {
              setDownloadProgress(event.loaded);
            });
          },
        });
      })
      .then((instance) => {
        setStatus("ready");
        return instance;
      })
      .catch((error) => {
        translatorRef.current = null;
        throw error;
      });

    translatorRef.current = creation;
    return creation;
  }, [enabled]);

  const translate = useCallback(
    async (text: string): Promise<string> => {
      if (!enabled) return text;

      const trimmed = text.trim();
      if (!trimmed) return text;

      const cached = memoryRef.current.get(trimmed);
      if (cached !== undefined) return cached;

      const translator = await getTranslator();
      const translated = await translator.translate(trimmed);
      memoryRef.current.set(trimmed, translated);
      return translated;
    },
    [enabled, getTranslator],
  );

  const setLang = useCallback(
    (next: Language) => {
      if (!enabled) return;
      setLangState(next);
      if (next === "en") setStatus((prev) => (prev === "unsupported" ? prev : "idle"));
    },
    [enabled],
  );

  const getCached = useCallback((text: string) => memoryRef.current.get(text.trim()), []);

  const value = useMemo<TranslationContextType>(
    () => ({ lang, status, downloadProgress, enabled, setLang, translate, getCached }),
    [lang, status, downloadProgress, enabled, setLang, translate, getCached],
  );

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
};

interface UseTranslatedTextOptions {
  capitalize?: boolean;
  titleCase?: boolean;
}

interface TranslatedTextState {
  value: string;
  isTranslating: boolean;
}

export const useTranslatedTextState = (
  text: string,
  options?: UseTranslatedTextOptions,
): TranslatedTextState => {
  const { lang, translate, getCached } = useTranslation();
  const shouldTranslate = lang === "en" && !!text && !!text.trim();
  const cached = shouldTranslate ? getCached(text) : undefined;

  const [resolved, setResolved] = useState<string | undefined>(cached);

  useEffect(() => {
    if (!shouldTranslate) {
      setResolved(undefined);
      return;
    }

    const hit = getCached(text);
    if (hit !== undefined) {
      setResolved(hit);
      return;
    }

    let cancelled = false;
    translate(text)
      .then((result) => {
        if (!cancelled) setResolved(result);
      })
      .catch(() => {
        if (!cancelled) setResolved(text);
      });

    return () => {
      cancelled = true;
    };
  }, [shouldTranslate, text, translate, getCached]);

  const base = !shouldTranslate ? text : cached ?? resolved ?? text;
  const isTranslating = shouldTranslate && cached === undefined && resolved === undefined;

  const value = options?.titleCase
    ? toTitleCase(base)
    : options?.capitalize
      ? toSentenceCase(base)
      : base;

  return { value, isTranslating };
};

export const useTranslatedText = (text: string, options?: UseTranslatedTextOptions): string =>
  useTranslatedTextState(text, options).value;

export default TranslationContext;
